import { Button, IconButton, Skeleton, Theme, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import { useMutation } from 'react-query';
import { useRecoilStateLoadable } from 'recoil';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import { ModalWithStyles } from './ModalWithStyles';

import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { DateTime } from 'luxon';
import { OptionsObject, SnackbarKey } from 'notistack';
import { spProductPricingEntity } from '../../../api/apigQuery';
import { methodNameEnum } from '../../../recoil/selectors/makePHPRequest';
import { IAlertWithProgressBar } from '../notifications/SnackbarProviderWithStyles';

const useStyles = makeStyles((theme: Theme) => ({
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  searchIconButton: {
    color: 'blue',
  },
  replaceIconButton: {
    color: 'purple',
  },
  addTrancheClick: {
    cursor: 'pointer',
  },
}));

export default function BoostSetMgmtModal({
  show,
  enqueueSnackbarWithStyles,
  refetchBusinessNumbers,
  handleCloseSearch,
  handleCloseNoSearch,
  handleBoostSetModal,
  boostSetPricing,
  trancheRes,
  isLoading,
}: {
  show: boolean;
  enqueueSnackbarWithStyles: (
    alertComponentProps: IAlertWithProgressBar,
    snackbarProps?: OptionsObject | undefined
  ) => SnackbarKey | null;
  refetchBusinessNumbers: () => void;
  handleCloseSearch: (e: string) => void;
  handleCloseNoSearch: () => void;
  handleBoostSetModal: React.Dispatch<React.SetStateAction<boolean>>;
  boostSetPricing: spProductPricingEntity;
  trancheRes:
    | {
        trancheData: {
          localPresenceTrancheId: string;
          created: string;
          businessproductid: number;
        }[];
      }
    | undefined;
  isLoading: boolean;
}) {
  const { proratedPrice, productBillingCycle: billingCycle, discountedPrice } =
    boostSetPricing || {};
  const price = discountedPrice! * billingCycle!;
  const classes = useStyles();
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable(user);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const mutation = useMutation(getApiMutation(userAtomContents, setUserAtom));

  const [showAddBoostSetModal, setShowAddBoostSetModal] = React.useState(false);
  const [showDeleteBoostSetModal, setShowDeleteBoostSetModal] = React.useState(
    false
  );

  const [businessDataCached] = makeApiRequestCached<
    Record<string, any> | undefined
  >(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );
  const [
    showReplaceBoostSetModal,
    setShowReplaceBoostSetModal,
  ] = React.useState(false);

  interface HandleDeleteInput {
    trancheId: string | number;
    businessproductid: string | number;
  }

  const [
    deleteTrancheId,
    setDeleteTrancheId,
  ] = React.useState<HandleDeleteInput | null>({
    trancheId: '',
    businessproductid: '',
  });

  const onBuyConnectionBoostStart = async () => {
    setIsSubmitting(true);
    try {
      const data = {
        apikey: userAtomContents?.apikey,
        businessid: userAtomContents?.businessid,
        userid: userAtomContents?.userid,
      };
      const response = await mutation.mutateAsync({
        endpoint: `${endpointEnum.TRANCHE_ACTION_HANDLER}/add`,
        payload: data,
        domain: process.env.VITE_APP_CDK_DOMAIN,
        method: 'post',
        stage: 'tele',
      });
      if (response.status === 202) {
        enqueueSnackbarWithStyles({
          message:
            'Connection boost set purchase successful. this may take up to 13 minutes. you will receive an email when complete.',
          alertProps: {
            severity: 'success',
          },
        });
      }
      setIsSubmitting(false);
      refetchBusinessNumbers();
      setShowAddBoostSetModal(false);
      setShowDeleteBoostSetModal(false);
      setShowReplaceBoostSetModal(false);
      handleCloseNoSearch();
      handleBoostSetModal(false);
    } catch (err) {
      setIsSubmitting(false);
      enqueueSnackbarWithStyles({
        message: 'Connection boost set purchase failed.',
        alertProps: {
          severity: 'error',
        },
      });
    }
  };

  const handleDeleteBoostSet = async (trancheIds: HandleDeleteInput | null) => {
    setIsSubmitting(true);
    try {
      const data = {
        apikey: userAtomContents?.apikey,
        businessid: userAtomContents?.businessid,
        userid: userAtomContents?.userid,
        trancheId: trancheIds?.trancheId,
        businessProductId: trancheIds?.businessproductid,
      };
      const response = await mutation.mutateAsync({
        endpoint: `${endpointEnum.TRANCHE_ACTION_HANDLER}/delete`,
        payload: data,
        domain: process.env.VITE_APP_CDK_DOMAIN,
        method: 'post',
        stage: 'tele',
      });
      if (response.status === 202) {
        enqueueSnackbarWithStyles({
          message:
            'Connection boost set delete successful. This may take several minutes to complete',
          alertProps: {
            severity: 'success',
          },
        });
      }
      setIsSubmitting(false);
      refetchBusinessNumbers();
      setShowAddBoostSetModal(false);
      setShowDeleteBoostSetModal(false);
      setShowReplaceBoostSetModal(false);
      setDeleteTrancheId(null);
      handleBoostSetModal(false);
      handleCloseNoSearch();
    } catch (err) {
      setIsSubmitting(false);
      enqueueSnackbarWithStyles({
        message: 'Connection boost set delete failed.',
        alertProps: {
          severity: 'error',
        },
      });
    }
  };
  const handleReplaceBoostSet = async (
    trancheIds: HandleDeleteInput | null
  ) => {
    setIsSubmitting(true);
    try {
      const data = {
        apikey: userAtomContents?.apikey,
        businessid: userAtomContents?.businessid,
        userid: userAtomContents?.userid,
        trancheId: trancheIds?.trancheId,
        businessProductId: trancheIds?.businessproductid,
        replaceTranche: true,
      };
      const response = await mutation.mutateAsync({
        endpoint: `${endpointEnum.TRANCHE_ACTION_HANDLER}/replace`,
        payload: data,
        domain: process.env.VITE_APP_CDK_DOMAIN,
        method: 'post',
        stage: 'tele',
      });
      if (response.status === 202) {
        enqueueSnackbarWithStyles({
          message:
            'Connection boost replace successful. This may take up to 25 minutes to complete.',
          alertProps: {
            severity: 'success',
          },
        });
      }
      setIsSubmitting(false);
      refetchBusinessNumbers();
      setShowAddBoostSetModal(false);
      setShowDeleteBoostSetModal(false);
      setShowReplaceBoostSetModal(false);
      setDeleteTrancheId(null);
      handleCloseNoSearch();
      handleBoostSetModal(false);
    } catch (err) {
      setIsSubmitting(false);
      enqueueSnackbarWithStyles({
        message: 'Connection boost set replace failed.',
        alertProps: {
          severity: 'error',
        },
      });
    }
  };
  return (
    <>
      <ModalWithStyles
        show={show}
        onSubmit={() => {}}
        aria-labelledby="example-modal-sizes-title-sm"
        title="Boost Set MGMT"
        onHide={() => {
          handleCloseNoSearch();
        }}
        isLoading={isSubmitting === true}
        size="xs"
        footerComponent={
          <>
            <Button
              variant="contained"
              disableElevation={true}
              onClick={() => handleCloseNoSearch()}
            >
              Close
            </Button>
          </>
        }
      >
        <>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 48px',
              }}
            ></div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0 48px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontWeight: 'bold' }}>Set ID</div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontWeight: 'bold', paddingLeft: '7px' }}>
                    Create Date
                  </div>
                </div>
                <div
                  style={{
                    fontWeight: 'bold',
                    minWidth: '100px',
                    paddingLeft: '12px',
                  }}
                >
                  Action
                </div>
              </div>
              {isLoading ? (
                <div>
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 5, marginTop: 20 }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    maxHeight: '450px',
                  }}
                >
                  {trancheRes?.trancheData?.map((t) => {
                    return (
                      <div
                        key={t.localPresenceTrancheId}
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0 48px',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            marginLeft: '11px',
                          }}
                        >
                          {t?.localPresenceTrancheId}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            marginLeft: '11px',
                          }}
                        >
                          {DateTime.fromISO(t?.created, {
                            zone: 'UTC',
                          })
                            .setZone(businessDataCached.current?.timezone_php)
                            .toFormat('MM/dd/yy')}
                        </div>
                        <div
                          style={{
                            textAlign: 'left',
                            paddingTop: '3px',
                            minWidth: '100px',
                          }}
                        >
                          <Tooltip title="Search Boost Sets">
                            <IconButton
                              className={classes.searchIconButton}
                              style={{ padding: '3px' }}
                              onClick={() => {
                                handleCloseSearch(t?.localPresenceTrancheId);
                              }}
                              size="large"
                            >
                              <SearchIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Replace Boost Set">
                            <IconButton
                              className={classes.replaceIconButton}
                              style={{ padding: '3px' }}
                              onClick={() => {
                                setDeleteTrancheId({
                                  trancheId: t?.localPresenceTrancheId,
                                  businessproductid: t?.businessproductid,
                                });
                                setShowReplaceBoostSetModal(true);
                              }}
                              size="large"
                            >
                              <SyncAltIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Boost Set">
                            <IconButton
                              className={classes.deleteIconButton}
                              style={{ padding: '3px' }}
                              onClick={() => {
                                setDeleteTrancheId({
                                  trancheId: t?.localPresenceTrancheId,
                                  businessproductid: t?.businessproductid,
                                });
                                setShowDeleteBoostSetModal(true);
                              }}
                              size="large"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ paddingLeft: '42px', paddingTop: '21px' }}>
                    <p
                      className={classes.addTrancheClick}
                      style={{ color: 'blue' }}
                      onClick={() => {
                        setShowAddBoostSetModal(true);
                      }}
                    >
                      +Add New Boost Set
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      </ModalWithStyles>
      <ModalWithStyles
        size="xs"
        show={showAddBoostSetModal}
        title="Add New Boost Set"
        onHide={() => {
          setShowAddBoostSetModal(false);
        }}
        submitLabel="Continue"
        onSubmit={onBuyConnectionBoostStart}
        isLoading={isSubmitting === true}
      >
        <p>{`You are about to add a new Boost Set to this account.`}</p>
        <p>
          {`A prorated charge of $${proratedPrice?.toFixed(
            2
          )} will be applied instantly and a new product in the amount of $${price} per ${
            billingCycle === 1 ? '' : billingCycle
          } month${
            billingCycle === 1 ? '' : 's'
          } will be applied to future billing cycles.`}
        </p>
        <p>Are you sure you want to continue?</p>
      </ModalWithStyles>
      <ModalWithStyles
        size="xs"
        show={showDeleteBoostSetModal}
        title="Delete Boost Set"
        onHide={() => {
          setShowDeleteBoostSetModal(false);
        }}
        submitLabel="Continue"
        onSubmit={async () => {
          await handleDeleteBoostSet(deleteTrancheId);
        }}
        isLoading={isSubmitting === true}
      >
        <p>{`You are about to remove Numbers associated with this Set ID ${deleteTrancheId?.trancheId}.`}</p>
        <p>{`This product will be removed from the next recurring billing.`}</p>
        <p>Are you sure you want to continue?</p>
      </ModalWithStyles>
      <ModalWithStyles
        size="xs"
        show={showReplaceBoostSetModal}
        title="Replace Boost Set"
        onHide={() => {
          setShowReplaceBoostSetModal(false);
        }}
        submitLabel="Continue"
        onSubmit={async () => {
          handleReplaceBoostSet(deleteTrancheId);
        }}
        isLoading={isSubmitting === true}
      >
        <p>{`You are about to remove all Numbers associated with Set ID ${deleteTrancheId?.trancheId} and replace them with a new Boost Set.`}</p>
        <p>
          {`A one-time fee of $250 will be charged to this account immediately with no other changes to recurring billing.`}
        </p>
        <p>Are you sure you want to continue?</p>
      </ModalWithStyles>
    </>
  );
}
